import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const buildQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const STATISTICS_REQUEST_TIMEOUT_MS = 45000;

const fetchStatistics = (scope, params = {}, timeout = STATISTICS_REQUEST_TIMEOUT_MS) => (
  safeFetch(`${API_URL}/api/statistics/${scope}${buildQuery(params)}`, {
    headers: getHeaders(),
  }, timeout)
);

const normalizeOldEmployeeStatistics = (legacy, params = {}) => {
  const stageFilter = params.stage || '';
  const stageRows = Array.isArray(legacy?.stages)
    ? legacy.stages
      .filter((row) => !stageFilter || row.stage_name === stageFilter)
      .map((row) => ({
        stage_name: row.stage_name,
        stage_label: row.stage_label,
        orders_count: Number(row.completed_count || 0),
        items_count: Number(row.items_count || 0),
        total_area: Number(row.total_area || 0),
        total_weight: Number(row.total_weight || 0),
        floors_count: 0,
        items_by_type: Object.entries(row.items_by_type || {})
          .map(([name, count]) => ({ name, count }))
          .sort((left, right) => Number(right.count || 0) - Number(left.count || 0)),
      }))
    : [];

  const processedOrders = stageRows.reduce((sum, row) => sum + Number(row.orders_count || 0), 0);
  const processedItems = stageRows.reduce((sum, row) => sum + Number(row.items_count || 0), 0);
  const processedArea = stageRows.reduce((sum, row) => sum + Number(row.total_area || 0), 0);
  const processedWeight = stageRows.reduce((sum, row) => sum + Number(row.total_weight || 0), 0);
  const additionalServices = Object.entries(legacy?.additional_services || {})
    .map(([name, count]) => ({ name, count }))
    .sort((left, right) => Number(right.count || 0) - Number(left.count || 0));
  const focusStage = stageRows.length
    ? stageRows.reduce((best, row) => (
      Number(row.items_count || 0) > Number(best.items_count || 0) ? row : best
    ))
    : null;

  return {
    scope: 'employee',
    user: {
      id: legacy?.user_id || null,
      name: legacy?.user_name || 'Сотрудник',
      user_type: null,
    },
    filters: {
      date_from: params.date_from || null,
      date_to: params.date_to || null,
      stage: stageFilter || null,
    },
    summary: {
      orders_created: Number(legacy?.total_orders_created || 0),
      orders_in_progress: 0,
      orders_completed: 0,
      orders_cancelled: 0,
      positions_created: Number(legacy?.total_items_created || 0),
      orders_without_items: Number(legacy?.orders_without_items || 0),
      stage_events: stageRows.length,
      processed_orders: processedOrders,
      processed_items: processedItems,
      processed_area: processedArea,
      processed_weight: processedWeight,
      additional_services_count: additionalServices.reduce((sum, row) => sum + Number(row.count || 0), 0),
      additional_services_created_count: additionalServices.reduce((sum, row) => sum + Number(row.count || 0), 0),
      additional_services_completed_count: 0,
      total_received: Number(legacy?.total_received || 0),
    },
    creation: {
      orders_count: Number(legacy?.total_orders_created || 0),
      positions_count: Number(legacy?.total_items_created || 0),
      orders_without_items: Number(legacy?.orders_without_items || 0),
      orders_in_progress: 0,
      orders_completed: 0,
      orders_cancelled: 0,
    },
    payment: {
      total_received: Number(legacy?.total_received || 0),
      cash_received: Number(legacy?.cash_received || 0),
      card_received: Number(legacy?.card_received || 0),
      unknown_received: Number(legacy?.total_received || 0) - Number(legacy?.cash_received || 0) - Number(legacy?.card_received || 0),
    },
    focus_stage: focusStage,
    stages: stageRows,
    repeat_stages: [],
    repeat_summary: {
      stage_events: 0,
      processed_orders: 0,
      processed_items: 0,
      rework_items: 0,
      total_area: 0,
      total_weight: 0,
    },
    additional_services: additionalServices,
    created_additional_services: additionalServices,
    completed_additional_services: [],
    repeat_completed_additional_services: [],
  };
};

export const getEmployeeStatistics = async (params = {}) => {
  try {
    return await fetchStatistics('employee', params);
  } catch (error) {
    if (error?.message !== 'Ресурс не найден' || !params.user_id) {
      throw error;
    }

    const legacy = await safeFetch(
      `${API_URL}/api/stats/employee/${params.user_id}${buildQuery({
        date_from: params.date_from,
        date_to: params.date_to,
      })}`,
      { headers: getHeaders() }
    );

    return normalizeOldEmployeeStatistics(legacy, params);
  }
};
export const getEmployeeStageDetails = (params = {}) => fetchStatistics('employee-stage-details', params);
export const getEmployeeOrderDetails = (params = {}) => fetchStatistics('employee-order-details', params);
export const getGeneralAdditionalServiceDetails = (params = {}) => fetchStatistics('general-additional-service-details', params);
export const getGeneralOrderDetails = (params = {}) => fetchStatistics('general-order-details', params);
export const getGeneralStageDetails = (params = {}) => fetchStatistics('general-stage-details', params);
export const getGeneralStatistics = (params = {}) => fetchStatistics('general', params);
export const getDiscountStatistics = (params = {}) => fetchStatistics('discounts', params);
export const getSourceStatistics = (params = {}) => fetchStatistics('sources', params);

export const getStatisticsUsers = async () => {
  const data = await safeFetch(`${API_URL}/api/statistics/users`, { headers: getHeaders() }, 15000);
  return data.users || [];
};

export const getCurrentAuthUser = () => safeFetch(`${API_URL}/api/check-auth`, { headers: getHeaders() });
